import { AlertCircle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { BillingStatus } from '../types/index';
import { Button } from './Button';

interface SubscriptionBannerProps {
  billing: BillingStatus | undefined
}

export function SubscriptionBanner({ billing }: SubscriptionBannerProps) {
  const navigate = useNavigate()

  if (!billing) return null

  const warning = (() => {
    if (billing.subscription_status === 'suspended') return 'suspended'
    if (billing.subscription_status === 'past_due') return 'past_due'
    if (billing.subscription_status === 'trialing' && billing.days_remaining <= 7)
      return 'trial_expiring'
    if (billing.subscription_status === 'active' && billing.days_remaining <= 7)
      return 'expiring'
    return null
  })()

  if (!warning) return null

  const config = {
    suspended: {
      bg: 'bg-red-50 border-red-200',
      icon: 'text-red-600',
      text: 'text-red-900',
      message: 'Your subscription has expired. Renew to regain access.',
      button: 'Renew Now',
    },
    past_due: {
      bg: 'bg-orange-50 border-orange-200',
      icon: 'text-orange-600',
      text: 'text-orange-900',
      message: `Payment failed. Your account is in grace period (${billing.days_remaining} days left). Please renew.`,
      button: 'Renew Now',
    },
    trial_expiring: {
      bg: 'bg-amber-50 border-amber-200',
      icon: 'text-amber-600',
      text: 'text-amber-900',
      message: `Your free trial expires in ${billing.days_remaining} days. Upgrade now to avoid interruption.`,
      button: 'Upgrade Now',
    },
    expiring: {
      bg: 'bg-yellow-50 border-yellow-200',
      icon: 'text-yellow-600',
      text: 'text-yellow-900',
      message: `Your subscription expires in ${billing.days_remaining} days. Renew to continue access.`,
      button: 'Renew Now',
    },
  }

  const cfg = config[warning as keyof typeof config]

  return (
    <div className={`${cfg.bg} border-l-4 p-4 mb-6`}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <AlertCircle className={`w-5 h-5 ${cfg.icon}`} />
          <p className={`${cfg.text} font-medium`}>{cfg.message}</p>
        </div>
        <Button
          variant="destructive"
          size="sm"
          onClick={() => navigate('/billing')}
        >
          {cfg.button}
        </Button>
      </div>
    </div>
  )
}
